import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuthHandling } from "./baseQuery";
import type {
  ContractsQuery,
  ContractsResponse,
  FilterOptions,
  ContractRow,
  ContractCreatePayload,
  ContractUpdatePayload,
  UsageSyncResult,
} from "../types/contracts";
import type { NotificationSummary } from "../types/notifications";

// All API responses are wrapped as { status, data } — transformResponse unwraps to the inner data shape.
export const contractsApi = createApi({
  reducerPath: "contractsApi",
  baseQuery: baseQueryWithAuthHandling,
  tagTypes: ["ContractList", "ContractFilterOptions", "NotificationSummary"],
  endpoints: (builder) => ({
    // Paginated, filtered list of contracts
    getContracts: builder.query<ContractsResponse, ContractsQuery>({
      query: ({
        page,
        pageSize,
        search,
        market,
        product,
        usage,
        contract,
        status,
        expiry,
      }) => ({
        url: "/contracts",
        params: {
          page,
          pageSize,
          search,
          market,
          product,
          usage,
          contract,
          status,
          expiry,
        },
      }),
      transformResponse: (raw: { status: string; data: ContractsResponse }) =>
        raw.data,
      providesTags: ["ContractList"],
    }),
    // Dropdown options for market, product, and legal entity filters
    getContractFilterOptions: builder.query<FilterOptions, void>({
      query: () => "/contracts/filter-options",
      transformResponse: (raw: { status: string; data: FilterOptions }) =>
        raw.data,
      providesTags: ["ContractFilterOptions"],
    }),
    // Create a new contract
    createContract: builder.mutation<ContractRow, ContractCreatePayload>({
      query: (body) => ({
        url: "/contracts",
        method: "POST",
        body,
      }),
      transformResponse: (raw: { status: string; data: ContractRow }) =>
        raw.data,
      invalidatesTags: ["ContractList", "NotificationSummary"],
    }),
    // Update an existing contract by id
    updateContract: builder.mutation<ContractRow, ContractUpdatePayload>({
      query: ({ id, ...body }) => ({
        url: `/contracts/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (raw: { status: string; data: ContractRow }) =>
        raw.data,
      invalidatesTags: ["ContractList", "NotificationSummary"],
    }),
    // Trigger usage sync across all source connectors
    syncUsage: builder.mutation<UsageSyncResult, void>({
      query: () => ({ url: "/usage-sync", method: "POST" }),
      transformResponse: (raw: { status: string; data: UsageSyncResult }) =>
        raw.data,
      invalidatesTags: ["ContractList", "NotificationSummary"],
    }),
    // Application-wide notification summary (counts of actionable items)
    getNotificationSummary: builder.query<NotificationSummary, void>({
      query: () => "/notifications/summary",
      transformResponse: (raw: { status: string; data: NotificationSummary }) =>
        raw.data,
      providesTags: ["NotificationSummary"],
    }),
  }),
});

export const {
  useGetContractsQuery,
  useGetContractFilterOptionsQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useSyncUsageMutation,
  useGetNotificationSummaryQuery,
} = contractsApi;
